import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  role: string;
  companyId: number;
  companyName: string;
  creditBalance: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateCreditBalance: (newBalance: number) => void;
  refreshCreditBalance: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // 서버에서 최신 크레딧 정보 가져오기 (내부용)
  const fetchLatestCreditBalance = React.useCallback(async (companyId: number) => {
    try {
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      const response = await axios.get(`${API_BASE_URL}/api/credit/balance/${companyId}`);

      if (response.data && response.data.success && response.data.data) {
        const newBalance = response.data.data.balance;
        setUser(prevUser => {
          if (!prevUser) return null;
          const updatedUser = { ...prevUser, creditBalance: newBalance };
          // 로컬 스토리지도 업데이트 (하지만 크레딧은 항상 서버에서 가져옴)
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  }, []);

  // 로컬 스토리지에서 사용자 정보 및 토큰 복원
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      const userData = JSON.parse(savedUser);
      setToken(savedToken);
      setUser(userData);

      // axios 기본 헤더에 토큰 설정
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

      // 페이지 로드 시 서버에서 최신 크레딧 정보 가져오기
      fetchLatestCreditBalance(userData.companyId);
    }

    // axios 인터셉터 설정 - 401 또는 토큰 만료 시 자동 로그아웃
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // 로그인 요청이나 크레딧 조회는 인터셉터에서 제외
        const isLoginRequest = error.config?.url?.includes('/api/auth/login');
        const isCreditRequest = error.config?.url?.includes('/api/credit/balance');

        if (!isLoginRequest && !isCreditRequest &&
            (error.response?.status === 401 ||
             error.response?.data?.message?.includes('expired') ||
             error.response?.data?.message?.includes('Lifetime validation failed'))) {
          console.log('Token expired, logging out...');
          // Clear auth state
          setUser(null);
          setToken(null);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];

          // 이미 로그인 페이지에 있지 않은 경우에만 리다이렉트
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // 클린업 함수
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [fetchLatestCreditBalance]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // 브라우저의 hostname을 사용하여 API URL 결정
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);
      const loginUrl = `${API_BASE_URL}/api/auth/login`;

      console.log('Attempting login to:', loginUrl);
      console.log('Email:', email);

      const response = await axios.post(loginUrl, {
        email,
        password
      });

      console.log('Login response:', response.data);

      if (response.data && response.data.token) {
        const { token: jwtToken, user: userData } = response.data;

        // axios 기본 헤더에 토큰 먼저 설정 (크레딧 조회 전에)
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

        // 로컬 스토리지에 저장
        localStorage.setItem('token', jwtToken);
        localStorage.setItem('user', JSON.stringify(userData));

        // 상태 업데이트
        setToken(jwtToken);
        setUser(userData);

        // 로그인 후 최신 크레딧 정보 가져오기 (백그라운드에서, 실패해도 로그인은 성공)
        fetchLatestCreditBalance(userData.companyId).catch(err => {
          console.warn('Failed to fetch credit balance after login:', err);
        });

        console.log('Login successful!');
        return true;
      }

      console.log('Login failed: No token in response');
      return false;
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');

    // axios 헤더에서 토큰 제거
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateCreditBalance = (newBalance: number) => {
    if (user) {
      const updatedUser = { ...user, creditBalance: newBalance };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const refreshCreditBalance = async () => {
    if (!user) return;
    await fetchLatestCreditBalance(user.companyId);
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    updateCreditBalance,
    refreshCreditBalance
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
